import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createEffect, createSignal, For, Show } from "solid-js";
import { MAP_CHANGE_STATUS, MAP_CHANGE_TYPE } from "../../enums/map";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import { MapActionEvidence } from "./MapActionEvidence";
import { MapSourceProvenance } from "./MapSourceProvenance";
import { mapStatusLabel } from "./mapLabels";
import type { MapPreviewAction } from "./mapPreviewActions";
import {
  mapActionCategoryLabel,
  mapActionEvidenceLabel,
  mapFutureBriefLabel,
} from "./mapPreviewConfig";
import type { MapPreviewSource } from "./mapPreviewModel";

export function MapSelectedChange(props: {
  data: OverviewResponse;
  websiteId: string;
  action: MapPreviewAction | null;
  source: MapPreviewSource;
  onAccept: () => void;
  onUnresolved: () => void;
  onDismiss: () => void;
  onAdjust: (trigger: HTMLElement) => void;
  onBrief: () => void;
}) {
  const [showAllPages, setShowAllPages] = createSignal(false);
  let activeId: string | null = null;
  const change = () => props.action?.change ?? null;
  createEffect(() => {
    if (change()?.id !== activeId) {
      activeId = change()?.id ?? null;
      setShowAllPages(false);
    }
  });
  const scoredPages = () =>
    change()?.pages.filter((page) => page.contentFitness !== null).length ?? 0;
  return (
    <aside class="map-selected-change" aria-label="Selected draft action">
      <Show
        when={props.action}
        keyed
        fallback={
          <div class="map-selected-empty">
            <span class="signal-mini-kicker">Draft architecture action</span>
            <h2>Choose a suggested direction</h2>
            <p>
              Select a card or target group to review its evidence, uncertainty
              and likely Brief handoff.
            </p>
          </div>
        }
      >
        {(action) => {
          const selected = action.change;
          return (
            <>
              <header class="map-selected-head">
                <div class="map-selected-badges">
                  <span class={`map-state-badge is-${selected.status}`}>
                    {mapStatusLabel(selected.status, selected.adjusted)}
                  </span>
                  <span
                    class={`map-evidence-badge is-${
                      action.evidenceSeverity ?? action.evidenceState
                    }`}
                  >
                    {mapActionEvidenceLabel(
                      action.evidenceState,
                      action.evidenceSeverity,
                    )}
                  </span>
                </div>
                <span class="map-selected-category">
                  {action.category
                    ? mapActionCategoryLabel(action.category)
                    : "Needs context"}
                  {" / "}
                  {mapFutureBriefLabel(action.futureBriefLevel)}
                </span>
                <h2>{selected.title}</h2>
                <p>{selected.summary}</p>
                <MapSourceProvenance source={props.source} compact />
              </header>

              <dl class="map-selected-facts">
                <div>
                  <dt>Affected pages</dt>
                  <dd>{selected.pages.length}</dd>
                </div>
                <div>
                  <dt>Assessment coverage</dt>
                  <dd>
                    {scoredPages()}/{selected.pages.length}
                  </dd>
                </div>
                <Show when={action.concernCount > 0}>
                  <div>
                    <dt>Causal concerns</dt>
                    <dd>{action.concernCount}</dd>
                  </div>
                </Show>
              </dl>

              <section class="map-selected-section">
                <h3>Current state</h3>
                <p>{selected.currentState}</p>
                <ul class="map-selected-pages">
                  <For
                    each={
                      showAllPages()
                        ? selected.pages
                        : selected.pages.slice(0, 4)
                    }
                  >
                    {(page) => (
                      <li>
                        <a href={`/w/${props.websiteId}/scan/pages/${page.id}`}>
                          {pageDisplayLabel(page, props.data.site)}
                        </a>
                        <small>{rawPagePath(page)}</small>
                      </li>
                    )}
                  </For>
                </ul>
                <Show when={selected.pages.length > 4}>
                  <button
                    type="button"
                    class="map-text-button"
                    onClick={() => setShowAllPages((value) => !value)}
                  >
                    {showAllPages()
                      ? "Show fewer pages"
                      : `View all ${selected.pages.length} pages`}
                  </button>
                </Show>
              </section>

              <section class="map-selected-section is-proposed">
                <h3>Draft suggested direction</h3>
                <p>{selected.proposedState}</p>
                <Show when={selected.clientNote} keyed>
                  {(note) => (
                    <p class="map-client-note">
                      <strong>Client note</strong>
                      {note}
                    </p>
                  )}
                </Show>
              </section>

              <MapActionEvidence action={action} websiteId={props.websiteId} />

              <section class="map-selected-section is-uncertain">
                <h3>What still needs validation</h3>
                <ul>
                  <For each={selected.uncertainties}>
                    {(uncertainty) => <li>{uncertainty}</li>}
                  </For>
                </ul>
              </section>

              <div class="map-decision-actions">
                <Show
                  when={selected.status === MAP_CHANGE_STATUS.ACCEPTED}
                  fallback={
                    <Show when={selected.type !== MAP_CHANGE_TYPE.UNRESOLVED}>
                      <button
                        type="button"
                        class="signal-dark-button"
                        onClick={props.onAccept}
                      >
                        Accept in preview
                      </button>
                    </Show>
                  }
                >
                  <button
                    type="button"
                    class="signal-dark-button"
                    onClick={() => props.onBrief()}
                  >
                    Preview in Brief
                  </button>
                </Show>
                <button
                  type="button"
                  onClick={(event) => props.onAdjust(event.currentTarget)}
                >
                  Adjust
                </button>
                <button type="button" onClick={props.onUnresolved}>
                  Needs context
                </button>
                <button
                  type="button"
                  class="is-dismiss"
                  onClick={props.onDismiss}
                >
                  Park
                </button>
              </div>
              <Show when={selected.status === MAP_CHANGE_STATUS.ACCEPTED}>
                <p class="map-ready-note">
                  <strong>Accepted in this session.</strong> This preview action
                  can be opened in Brief as unsaved draft input.
                </p>
              </Show>
            </>
          );
        }}
      </Show>
    </aside>
  );
}
